import { api } from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type {
  AnalyticsV2QueryParams,
  AnalyticsV2SeriesQueryParams,
  CoverageFunnelResult,
  ScalarMetricKey,
  ScalarMetricsBatch,
  SeriesMetricKey,
  SeriesMetricsBatch,
  TicketBreakdownQueryParams,
  TicketBreakdownRow,
} from '@/types/analyticsV2';

interface ApiEnvelope<T> {
  statusCode: number;
  success: boolean;
  message: string;
  data: T;
}

/**
 * Brand scope on the wire. Absent rather than empty when unscoped: kim-analytics reads a
 * missing `buId` as account-wide, and `buId=` would work by accident rather than by
 * contract — its schema rejects a value that is not a positive integer.
 */
const buScopeParam = (buId: string | null | undefined): { buId?: string } =>
  buId ? { buId } : {};

export const analyticsV2Service = {
  /**
   * Batch-fetch one or more scalar metrics for an account/date-range. Server
   * runs the queries in parallel and returns a record keyed by metric key.
   * Wire format is camelCase end-to-end — no adapter needed.
   */
  async getMetrics(
    keys: ScalarMetricKey[],
    params: AnalyticsV2QueryParams,
  ): Promise<ScalarMetricsBatch> {
    const response = await api.get<ApiEnvelope<ScalarMetricsBatch>>(
      API_ENDPOINTS.ANALYTICS_V2.METRICS,
      {
        params: {
          keys: keys.join(','),
          fromDate: params.fromDate,
          toDate: params.toDate,
          ...(params.accountId != null ? { accountId: params.accountId } : {}),
          ...buScopeParam(params.buId),
        },
      },
    );
    return response.data.data;
  },

  /**
   * Batch-fetch one or more series metrics. Same shape contract as getMetrics —
   * one entry per requested key — plus a granularity bucket per series.
   */
  async getSeries(
    keys: SeriesMetricKey[],
    params: AnalyticsV2SeriesQueryParams,
  ): Promise<SeriesMetricsBatch> {
    const response = await api.get<ApiEnvelope<SeriesMetricsBatch>>(
      API_ENDPOINTS.ANALYTICS_V2.METRICS_SERIES,
      {
        params: {
          keys: keys.join(','),
          fromDate: params.fromDate,
          toDate: params.toDate,
          granularity: params.granularity,
          ...(params.accountId != null ? { accountId: params.accountId } : {}),
          ...buScopeParam(params.buId),
        },
      },
    );
    return response.data.data;
  },

  /** CS tickets grouped by one level of their classification, inside the given category / subcategory. */
  async getTicketBreakdown(params: TicketBreakdownQueryParams): Promise<TicketBreakdownRow[]> {
    const response = await api.get<ApiEnvelope<TicketBreakdownRow[]>>(
      API_ENDPOINTS.ANALYTICS_V2.CATEGORIES,
      {
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          by: params.by,
          ...(params.category != null ? { category: params.category } : {}),
          ...(params.subcategory != null ? { subcategory: params.subcategory } : {}),
          ...(params.accountId != null ? { accountId: params.accountId } : {}),
          ...buScopeParam(params.buId),
        },
      },
    );
    return response.data.data;
  },

  /**
   * Coverage Funnel for the super-admin Internal Analytics page. Server returns
   * the seven descending stages (csTickets baseline down to Agent Sent) plus
   * the HITL approved/rejected/pending split, mirroring the shadow report's
   * Coverage Funnel sheet. `accountId` is honored for super admins exactly like
   * the /metrics proxy.
   */
  async getFunnel(
    params: AnalyticsV2QueryParams,
  ): Promise<CoverageFunnelResult> {
    const response = await api.get<ApiEnvelope<CoverageFunnelResult>>(
      API_ENDPOINTS.ANALYTICS_V2.FUNNEL,
      {
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          ...(params.accountId != null ? { accountId: params.accountId } : {}),
          ...buScopeParam(params.buId),
        },
      },
    );
    return response.data.data;
  },
};
